import { useEffect, useRef, useState } from 'react'

// Plain text box that works immediately. If Google Places has loaded, it
// shows suburb suggestions underneath; if not (slow connection, blocked
// script), the typed suburb is used as-is. Never blocks the form.
// Optional onPlace({ suburb, postcode, state, lat, lng }) is called when a
// suggestion is picked (used by the Wanted form for postcode + map point).
export default function SuburbInput({ value, onChange, hasError, id, onPlace }) {
const [suggestions, setSuggestions] = useState([])
const [open, setOpen] = useState(false)
const tokenRef = useRef(null)
const reqRef = useRef(0)

useEffect(() => {
const text = (value || '').trim()
const Places = window.google?.maps?.places
if (!open || text.length < 2 || !Places?.AutocompleteSuggestion) { setSuggestions([]); return }
const reqId = ++reqRef.current
const t = setTimeout(async () => {
try {
if (!tokenRef.current && Places.AutocompleteSessionToken) tokenRef.current = new Places.AutocompleteSessionToken()
const { suggestions: found } = await Places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
input: text,
includedPrimaryTypes: ['(regions)'],
includedRegionCodes: ['au'],
sessionToken: tokenRef.current || undefined,
})
if (reqId !== reqRef.current) return
setSuggestions((found || []).map(s => s.placePrediction).filter(Boolean).slice(0, 5).map(p => ({
main: p.mainText?.text || p.text?.text || '',
full: p.text?.text || p.mainText?.text || '',
prediction: p,
})))
} catch {
if (reqId === reqRef.current) setSuggestions([])
}
}, 250)
return () => clearTimeout(t)
}, [value, open])

const pick = async (s) => {
onChange(s.main)
setSuggestions([])
setOpen(false)
tokenRef.current = null
if (!onPlace) return
try {
const place = s.prediction.toPlace()
await place.fetchFields({ fields: ['location', 'addressComponents'] })
const get = (type, short) => place.addressComponents?.find(c => c.types.includes(type))?.[short ? 'shortText' : 'longText'] || ''
onPlace({ suburb: s.main, postcode: get('postal_code'), state: get('administrative_area_level_1', true), lat: place.location?.lat(), lng: place.location?.lng() })
} catch {
onPlace({ suburb: s.main })
}
}

return (
<div style={{ position: 'relative' }}>
<input
id={id}
className="form-control"
placeholder="e.g. Palmwoods"
value={value}
onChange={e => { onChange(e.target.value); setOpen(true) }}
onFocus={() => setOpen(true)}
onBlur={() => setTimeout(() => setOpen(false), 150)}
autoComplete="address-level2"
autoCapitalize="words"
aria-invalid={hasError || undefined}
style={hasError ? { borderColor: 'var(--red)' } : undefined}
/>
{open && suggestions.length > 0 && (
<div role="listbox" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: 'white', border: '1px solid var(--border)', borderRadius: 10, marginTop: 4, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
{suggestions.map((s, i) => (
<button
key={i}
type="button"
role="option"
aria-selected={false}
onMouseDown={e => { e.preventDefault(); pick(s) }}
style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', borderTop: i ? '1px solid var(--border)' : 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text)' }}
>
{s.full}
</button>
))}
</div>
)}
</div>
)
}
